import { CaretDoubleLeftIcon, CaretDoubleRightIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField, NumberScroll, PaginationButton, Text } from '@components';
import { DIMENSIONS } from '@constants';
import { Dictionaries, InputType, Translation } from '@enums';
import { cn, getOptionListFromCatalog } from '@utils';

const pageButtonVariants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.8, opacity: 0 },
  hover: { scale: 1.1 },
  tap: { scale: 0.95 },
};

interface PaginationProps {
  readonly first_page: number;
  readonly last_page: number;
  readonly next_page: number;
  readonly page: number;
  readonly total: number;
  readonly total_pages: number;
  readonly page_size: number;
}

interface PaginationComponentProps {
  pagination: PaginationProps;
  onChangePage: (page: number) => void;
  onChangePageSize?: (page_size: number) => void;
  isLoading?: boolean;
  className?: string;
}

const containerVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.05,
    },
  },
  exit: { opacity: 0, y: -20 },
};

const calculateVisiblePages = (currentPage: number, totalPages: number): number[] => {
  if (totalPages <= 2) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 2) return [1, 2, 3];
  if (currentPage >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];

  return [currentPage - 1, currentPage, currentPage + 1];
};

const Pagination: React.FC<PaginationComponentProps> = ({
  pagination,
  onChangePage,
  onChangePageSize,
  isLoading = false,
  className = '',
}) => {
  const { t } = useTranslation(Translation.Common, { keyPrefix: 'tables' });
  const {
    page: currentPage,
    first_page: firstPage,
    total_pages: totalPages,
    last_page: lastPage,
    page_size: pageSize,
    total,
  } = pagination;

  const visiblePages = useMemo(() => calculateVisiblePages(currentPage, totalPages), [currentPage, totalPages]);

  const currentPageItemCount = useCallback(() => {
    if (!pagination?.page_size || !pagination?.total || !pagination?.page || !pagination?.total_pages) {
      return 0;
    }

    return pagination.page === pagination.total_pages
      ? pagination.total % pagination.page_size || pagination.page_size
      : pagination.page_size;
  }, [pagination])();

  const isFirstPage = currentPage === firstPage;
  const isLastPage = currentPage === totalPages;

  const handlePageClick = useCallback(
    (page: number) => {
      if (page !== currentPage && !isLoading) {
        onChangePage(page);
      }
    },
    [currentPage, isLoading, onChangePage],
  );

  const handlePageSizeClick = useCallback(
    (e: any) => {
      if (!!onChangePageSize && e.target.value !== pageSize && !isLoading) {
        onChangePageSize(e.target.value);
      }
    },
    [currentPage, isLoading, onChangePage],
  );

  const renderPageInfo = useMemo(
    () => (
      <motion.div className="flex items-center text-gray-500">
        <Text $level={6} className="whitespace-nowrap">
          {t('showingRows')}
          <motion.span layout className="font-medium mx-1.5 text-gray-700">
            <NumberScroll value={(currentPage - 1) * pageSize + 1} />
          </motion.span>
          {t('to')}
          <motion.span layout className="font-medium mx-1.5 text-gray-700">
            <NumberScroll value={Math.min(currentPage * pageSize, total)} />
          </motion.span>
          {t('of')}
          <motion.span layout className="font-medium mx-1.5 text-gray-700">
            <NumberScroll value={total} />
          </motion.span>
        </Text>
      </motion.div>
    ),
    [currentPage, pageSize, total, t],
  );

  if (total === 0) return null;
  if (totalPages < firstPage) {
    return null;
  }

  return (
    <motion.div
      className={cn(
        'flex flex-col md:flex-row items-center justify-between px-4 py-3 gap-3',
        currentPageItemCount % 2 !== 0 && 'bg-gray-50',
        (currentPageItemCount % 2 === 0 || currentPageItemCount === 0) && 'bg-white',
        className,
      )}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="flex flex-col sm:flex-row items-center sm:gap-4 w-full justify-between">
        <AnimatePresence mode="wait">
          <motion.nav
            className="isolate inline-flex space-x-1 items-center rounded-md"
            role="navigation"
            aria-label="Pagination"
          >
            {totalPages >= 2 && (
              <PaginationButton
                onClick={() => handlePageClick(firstPage)}
                disabled={isFirstPage || isLoading}
                ariaLabel="Previous page"
              >
                <CaretDoubleLeftIcon
                  weight="bold"
                  className={`size-4 ${isFirstPage ? 'text-gray-300' : 'text-theme-primary-main'}`}
                  aria-hidden="true"
                />
              </PaginationButton>
            )}
            <PaginationButton
              onClick={() => handlePageClick(currentPage - 1)}
              disabled={isFirstPage || isLoading}
              ariaLabel="Previous page"
            >
              <CaretLeftIcon
                weight="bold"
                className={`size-4 ${isFirstPage ? 'text-gray-300' : 'text-theme-primary-main'}`}
              />
            </PaginationButton>
            {currentPage > 2 && totalPages > DIMENSIONS.MIN_PAGES_FOR_ELLIPSIS && (
              <PaginationButton onClick={() => handlePageClick(1)} disabled={isLoading} data-testid="first-page-button">
                1
              </PaginationButton>
            )}
            {currentPage > 3 && totalPages > DIMENSIONS.MIN_PAGES_FOR_ELLIPSIS && (
              <motion.span
                role="separator"
                aria-hidden="true"
                variants={pageButtonVariants}
                className="px-2 text-gray-500"
              >
                ...
              </motion.span>
            )}
            {visiblePages.map((page) => (
              <PaginationButton
                key={page}
                onClick={() => handlePageClick(page)}
                disabled={page === currentPage || isLoading}
                active={page === currentPage}
                aria-current={page === currentPage ? 'page' : undefined}
                data-testid={`page-button-${page}`}
              >
                {page}
              </PaginationButton>
            ))}
            {currentPage < totalPages - 2 && totalPages > DIMENSIONS.MIN_PAGES_FOR_ELLIPSIS && (
              <motion.span
                role="separator"
                aria-hidden="true"
                variants={pageButtonVariants}
                className="px-2 text-gray-500"
              >
                ...
              </motion.span>
            )}
            {currentPage < totalPages - 1 && totalPages > 3 && (
              <PaginationButton
                onClick={() => handlePageClick(totalPages)}
                disabled={isLoading}
                data-testid="last-page-button"
              >
                {totalPages}
              </PaginationButton>
            )}
            <PaginationButton
              onClick={() => handlePageClick(currentPage + 1)}
              disabled={isLastPage || isLoading}
              ariaLabel="Next page"
            >
              <CaretRightIcon
                weight="bold"
                className={`size-4 ${isLastPage ? 'text-gray-300' : 'text-theme-primary-main'}`}
              />
            </PaginationButton>
            {totalPages >= 2 && (
              <PaginationButton
                onClick={() => handlePageClick(lastPage)}
                disabled={isLastPage || isLoading}
                ariaLabel="Next page"
              >
                <CaretDoubleRightIcon
                  weight="bold"
                  className={`size-4 ${isLastPage ? 'text-gray-300' : 'text-theme-primary-main'}`}
                  aria-hidden="true"
                />
              </PaginationButton>
            )}
          </motion.nav>
        </AnimatePresence>
        <div className="flex gap-4">
          {!!onChangePageSize && (
            <FormField
              withoutLabel
              options={getOptionListFromCatalog({ name: Dictionaries.PageSize })}
              onChange={(value) => handlePageSizeClick(value)}
              value={pageSize}
              name="page_size"
              textInputClassName="w-14 bg-white pr-0 py-0.5 ring-1 focus:ring-1 ring-gray-200"
              inputType={InputType.Select}
            />
          )}
          {renderPageInfo}
        </div>
      </div>

      <div className="block md:hidden text-center">{renderPageInfo}</div>
    </motion.div>
  );
};

export default memo(Pagination);
